// Arabic catalogue — DEVELOPMENT DRAFT by Claude (PD-011). Not reviewed or approved for release;
// the interface labels it as unreviewed until decision H-10 names a reviewer. Identifiers, codes,
// email addresses and time-zone names stay Latin and are isolated left-to-right in the UI (PD-012).
import { arM2 } from './ar-m2.ts';
import { arM3 } from './ar-m3.ts';
import { arOps } from './ar-ops.ts';
import { arIdentity } from './ar-identity.ts';
import { arContractors } from './ar-contractors.ts';
import { arSafety } from './ar-safety.ts';
import { arCredentials } from './ar-credentials.ts';
import { arVehicles } from './ar-vehicles.ts';
import { arPeople } from './ar-people.ts';
import { arPrivacy } from './ar-privacy.ts';
import { arVerification } from './ar-verification.ts';
import { arCalendars } from './ar-calendars.ts';
import { arAdapters } from './ar-adapters.ts'; // ADR-025 (adapters & edge stream)
import { arAi } from './ar-ai.ts';
import { arAiprov } from './ar-aiprov.ts'; // external AI provider (aiprov stream, PD-600 – PD-606)
import { arReceptionGaps } from './ar-reception-gaps.ts'; // reception remainders (PD-411 – PD-413)
import { arReceptionLookup } from './ar-reception-lookup.ts'; // PB-06-02 lookup fields (PD-492 – PD-495)
import { arModel } from './ar-model.ts'; // departments, HR mapping, site retention (PD-540 – PD-552)
import { arEmail } from './ar-email.ts'; // email delivery settings (PD-580 – PD-589)
import { arOpsview } from './ar-opsview.ts'; // operations views stream (PD-555 – PD-569)
import { arPortal } from './ar-portal.ts'; // portal stream: receipts, UAE PASS signing, device printing (PD-510 – PD-515)
import { arRecurrence } from './ar-recurrence.ts'; // recurrence & rooms stream (PD-470 – PD-479)
import { arOpsui } from './ar-opsui.ts'; // operations-console stream (ADR-029)
import { arWorkflows } from './ar-workflows.ts'; // visitor-type workflows stream (ADR-027)
import { arOrgs } from './ar-orgs.ts'; // people & organisations stream (ADR-028)
import { arDocuments } from './ar-documents.ts'; // documents stream (PB-05-07, PB-09-08, PB-07-09)
import { arMsg } from './ar-msg.ts'; // messaging, correlation and reports stream (PD-525 – PD-539)
import { arPdf } from './ar-pdf.ts'; // native PDF exports (PD-635 – PD-640)
import { arLdap } from './ar-ldap.ts'; // LDAP / Active Directory directory stream (PD-590 – PD-597)
import { arPriv } from './ar-priv.ts'; // privacy values stream: retroactive minimisation, legal bases (PD-610 – PD-624)
import { arClamav } from './ar-clamav.ts'; // clamav stream: malware checks of uploads (PD-707, PD-645 – PD-654)
import { arDeploy } from './ar-deploy.ts'; // deploy stream (PD-655 – PD-664)
import { arHardening } from './ar-hardening.ts'; // hardening stream (PD-790 – PD-795)
import { arSettings } from './ar-settings.ts'; // settings stream (PD-710, PD-665 – PD-689)
import { arDemoguard } from './ar-demoguard.ts'; // demoguard stream: demo mode (PD-714, PD-720 – PD-739)
import { arDemodata } from './ar-demodata.ts'; // demodata stream: demo account list (PD-740 – PD-759)
import { arDemosandbox } from './ar-demosandbox.ts'; // demosandbox stream: per-browser demo sandbox (PD-763, PD-770 – PD-789)
import { arDemostaff } from './ar-demostaff.ts'; // demostaff stream: staff and console demo sandboxes (PD-800, PD-810 – PD-829)
import type { Catalogue } from './types.ts';

export const ar: Catalogue = {
  ...arM2,
  ...arM3,
  ...arOps,
  ...arIdentity,
  ...arContractors,
  ...arSafety,
  ...arCredentials,
  ...arVehicles,
  ...arPeople, // people & records stream (ADR-023)
  ...arPrivacy,
  ...arVerification,
  ...arCalendars,
  ...arAdapters,
  ...arDocuments,
  ...arAi, // AI add-ons stream (ADR-024)
  ...arAiprov, // external AI provider: provider key, connection test, personal-data rule
  ...arReceptionGaps, // reception remainders (PB-06-04, PB-06-12, PB-04-07)
  ...arReceptionLookup, // PB-06-02 lookup fields
  ...arModel, // PB-18-07 departments and HR mapping, PB-12-05 site retention
  ...arEmail, // PB-13-04 email delivery settings (email stream)
  ...arOpsview, // operations views: integration health, recovery evidence, visit access events, configuration promotion
  ...arPortal, // portal stream (PD-510 – PD-515)
  ...arRecurrence, // recurring visits and room booking policy (PB-04-02, PB-12-03)
  ...arOpsui, // operations-console stream (ADR-029)
  ...arWorkflows, // visitor-type workflows stream (ADR-027)
  ...arOrgs, // people & organisations stream (ADR-028)
  ...arMsg, // messaging, correlation and reports stream (PB-04-08, PB-09-14, PB-15-13, OR-11)
  ...arPdf, // native PDF exports (OR-11, PD-635 – PD-640)
  ...arLdap, // LDAP / Active Directory directory sync (PB-17-07, PB-03-09)
  ...arPriv, // privacy values stream (PB-16-03, H-04)
  ...arClamav, // clamav stream: malware checks of uploads (PD-707)
  ...arDeploy, // deploy stream: UAE pilot profile (PD-663)
  ...arHardening, // hardening stream: re-seal runs, identity-provider destinations (PD-791, PD-795)
  ...arSettings, // settings stream: installation settings in the platform console (PD-710)
  ...arDemoguard, // demoguard stream: demo mode, read-only and nothing sent (PD-714)
  ...arDemodata, // demodata stream: demo account list on the sign-in pages (PD-744, PD-749)
  ...arDemosandbox, // demosandbox stream: kiosk and pass answers appear to work in demo mode (PD-763)
  ...arDemostaff, // demostaff stream: staff and console changes appear to work in demo mode (PD-800)
  // ------------------------------------------------------------ approved role experiences
  'role.org-admin.title': 'مسؤول المؤسسة',
  'role.org-admin.heading': 'أساس متّسق لكل موقع.',
  'role.org-admin.intro': 'أدِر الأشخاص والسياسات وإطار التشغيل المشترك. الصلاحيات التشغيلية وصلاحيات الخصوصية تُمنح بشكل منفصل.',
  'role.org-admin.boundary': 'لا يشمل إدخال الزوار أو تزويد صلاحيات الدخول أو حذف بيانات الخصوصية أو قيادة الحوادث.',
  'role.org-admin.primary': 'مراجعة المستخدمين',
  'role.org-admin.task1.title': 'وصول المستخدمين',
  'role.org-admin.task1.body': 'راجِع الحسابات النشطة واحمِ آخر مسؤول.',
  'role.org-admin.task2.title': 'إصدارات السياسات',
  'role.org-admin.task2.body': 'صِغ نمط الموافقات؛ هذا لا يطبّق سياسة فعلية بعد.',
  'role.org-admin.task3.title': 'إطار المواقع',
  'role.org-admin.task3.body': 'راجِع المواقع وسعة القاعات.',
  'role.site-admin.title': 'مسؤول الموقع',
  'role.site-admin.heading': 'اجعل موقعك جاهزاً للترحيب.',
  'role.site-admin.intro': 'حافِظ على المساحات المحلية وتعليمات التشغيل دون اكتساب صلاحيات أمنية على مستوى المؤسسة.',
  'role.site-admin.boundary': 'لا يشمل المستخدمين على مستوى المؤسسة أو قرارات الموافقة أو أوامر الدخول أو حالات الخصوصية.',
  'role.site-admin.primary': 'إدارة المساحات',
  'role.site-admin.task1.title': 'القاعات والمساحات',
  'role.site-admin.task1.body': 'راجِع السعة وأضِف مساحة اجتماعات محلية.',
  'role.site-admin.task2.title': 'التواصل المحلي',
  'role.site-admin.task2.body': 'راجِع القوالب ومسودات التوجيه؛ لا تُرسَل أي رسائل.',
  'role.host.title': 'المضيف / الموظف',
  'role.host.heading': 'ضيوفك. خطة واحدة واضحة.',
  'role.host.intro': 'ادعُ الزوار وحدّث الخطط وتابِع رحلة الوصول. تعرض شاشتك الزيارات التي تستضيفها فقط.',
  'role.host.boundary': 'لا يمكنك الموافقة على طلباتك أو التحقق من الهوية أو إدخال الزوار أو الاطلاع على سجلات مضيف آخر.',
  'role.host.primary': 'دعوة زائر',
  'role.host.task1.title': 'دعواتك',
  'role.host.task1.body': 'أنشئ زيارة أو عدّلها أو ألغِها وتابِع حالاتها المستقلة.',
  'role.reception.title': 'الاستقبال',
  'role.reception.heading': 'اجعل كل وصول سلساً.',
  'role.reception.intro': 'اعرف من هو متوقّع، وعالِج المتطلبات الناقصة، وسجّل الوصول والمغادرة الفعليين.',
  'role.reception.boundary': 'لا يمكن الموافقة على الزيارات أو تأكيد نتيجة الدخول المنطقي. استثناءات بطاقات الدخول تُحال إلى مشغّل المنصة.',
  'role.reception.primary': 'فتح مكتب الوصول',
  'role.reception.task1.title': 'مكتب الوصول',
  'role.reception.task1.body': 'تحقّق من الأدلة وسجّل الحضور المرصود.',
  'role.reception.task2.title': 'العهدة المادية',
  'role.reception.task2.body': 'سلّم بطاقة أو استرجعها دون أن يعني ذلك منح دخول منطقي.',
  'role.reception.task3.title': 'تسليم الطرود',
  'role.reception.task3.body': 'تتبّع الطرد بشكل منفصل عن المندوب.',
  'role.approver.title': 'المعتمِد',
  'role.approver.heading': 'قرارات واضحة. أسباب موثّقة.',
  'role.approver.intro': 'راجِع الطلبات المعلّقة وسجّل القرار وأبقِ الموافقة منفصلة عن الهوية والإدخال.',
  'role.approver.boundary': 'لا يمكنك الموافقة على طلبات تستضيفها بنفسك؛ تحتاج إلى معتمِد آخر مكلَّف.',
  'role.approver.primary': 'مراجعة الطلبات',
  'role.approver.task1.title': 'صندوق الموافقات',
  'role.approver.task1.body': 'اتخذ قراراً مسبَّباً بشأن طلب يملكه مضيف آخر.',
  'role.guard.title': 'الحارس / مشغّل البوابة',
  'role.guard.heading': 'اعرف العائق قبل الحاجز.',
  'role.guard.intro': 'تحقّق من الزيارة المرتبطة وسجّل ملاحظة البوابة. التعرّف على مركبة لا يعني الإذن بالدخول أبداً.',
  'role.guard.boundary': 'لا يشمل دليل الزوار أو أدلة الهوية أو تغيير السياسات أو التشغيل عن بُعد للحاجز.',
  'role.guard.primary': 'مراجعة قائمة البوابة',
  'role.guard.task1.title': 'فحوصات البوابة',
  'role.guard.task1.body': 'تبقى اللوحة وأهلية الزيارة والحركة المرصودة منفصلة.',
  'role.permit.title': 'مراجع المقاولين / التصاريح',
  'role.permit.heading': 'الأدلة الصحيحة قبل بدء العمل.',
  'role.permit.intro': 'راجِع أدلة المقاولين وتفويض العمل المحدود. التصريح لا يغني عن فحوصات كل زائر.',
  'role.permit.boundary': 'لا يمكن منح دخول منطقي أو تسجيل مغادرة الأشخاص. الإيقاف ينشئ أعمال دخول معلّقة عند الحاجة.',
  'role.permit.primary': 'مراجعة تصاريح العمل',
  'role.permit.task1.title': 'حزم العمل',
  'role.permit.task1.body': 'اقبل الأدلة قبل اعتماد التصريح؛ وافحص أثر الإيقاف.',
  'role.emergency.title': 'منسّق الطوارئ',
  'role.emergency.heading': 'احصِ الأشخاص. وأظهِر مواطن عدم اليقين.',
  'role.emergency.intro': 'ابدأ تجمّع إخلاء، وراجِع القائمة الملتقطة، وسجّل الملاحظات الفردية. Passage ليس خدمة طوارئ.',
  'role.emergency.boundary': 'لا يطلق الإنذارات ولا يضمن الموقع الفعلي ولا يعيد كتابة حضور الزوار من ملاحظة نداء الأسماء.',
  'role.emergency.primary': 'فتح التجمّع',
  'role.emergency.task1.title': 'تنسيق التجمّع',
  'role.emergency.task1.body': 'التقط قائمة، واحصِ كل شخص، وأغلِق مع ذكر السبب.',
  'role.analyst.title': 'المحلّل / تقارير فقط',
  'role.analyst.heading': 'افهم العمليات، لا الهويات.',
  'role.analyst.intro': 'راجِع الإجماليات ضمن النطاق وأعداد الحالات المستقلة. هذا الملف المقترح للتقارير لا يتضمن تصديراً على مستوى الأفراد.',
  'role.analyst.boundary': 'لا أسماء زوار ولا سجل أحداث خام ولا تصدير مجدول ولا تغييرات تشغيلية. يبقى الذكاء الاصطناعي إضافة اختيارية لفئة Enterprise+.',
  'role.analyst.primary': 'عرض التقرير الإجمالي',
  'role.analyst.task1.title': 'الصورة التشغيلية اليومية',
  'role.analyst.task1.body': 'قارِن الحضور المرصود والموافقات المعلّقة ونتائج الدخول غير المعروفة.',
  'role.auditor.title': 'المدقّق',
  'role.auditor.heading': 'تتبّع الأدلة دون تغييرها.',
  'role.auditor.intro': 'راجِع من فعل ماذا وأين يبقى عدم اليقين. سجل التدقيق قابل للإضافة فقط.',
  'role.auditor.boundary': 'لا قرارات تشغيلية ولا إدارة مستخدمين ولا تعديل حالات الخصوصية ولا تغيير للسجلات.',
  'role.auditor.primary': 'فحص سجل التدقيق',
  'role.auditor.task1.title': 'مراجعة الأحداث',
  'role.auditor.task1.body': 'افحص المنفّذ والإجراء والنطاق ومرجع السجل.',
  'role.privacy.title': 'مراجع الخصوصية',
  'role.privacy.heading': 'احترم الطلب. وأثبِت النتيجة.',
  'role.privacy.intro': 'تحقّق من مقدّم الطلب، وراجِع الحجز، وتتبّع نتيجة كل مخزن قبل إغلاق الحالة.',
  'role.privacy.boundary': 'لا حذف شامل للسجلات ولا إدارة مستخدمين ولا افتراض اكتمال الأنظمة اللاحقة. فترات الاحتفاظ ما زالت غير محسومة.',
  'role.privacy.primary': 'فتح طلبات أصحاب البيانات',
  'role.privacy.task1.title': 'طلبات أصحاب البيانات',
  'role.privacy.task1.body': 'الهوية والنطاق والحجز ونتائج المخازن خطوات مراجعة منفصلة.',
  'role.operator.title': 'مشغّل التكاملات / المنصة',
  'role.operator.heading': 'استعِد النتيجة، لا الاتصال فقط.',
  'role.operator.intro': 'افحص سلامة الاعتماديات وطابِق بطاقات الدخول غير المؤكدة باستخدام نتائج قراءة موثوقة.',
  'role.operator.boundary': 'لا إدخال روتيني للزوار ولا موافقات ولا حذف للخصوصية ولا منح صلاحيات إدارة المستخدمين.',
  'role.operator.primary': 'فتح قائمة الاستعادة',
  'role.operator.task1.title': 'استعادة الدخول',
  'role.operator.task1.body': 'أكّد الإلغاء قبل الإفراج عن بطاقة محجوزة.',
  'role.operator.task2.title': 'جاهزية المزوّدين',
  'role.operator.task2.body': 'افحص العمليات المدعومة ومتطلبات القراءة الموثوقة.',
  'role.operator.task3.title': 'الأعمال الخلفية',
  'role.operator.task3.body': 'أعِد المحاولة أو انقل العمل إلى قائمة الأخطاء؛ سلامة الاتصال ليست نجاحاً.',
  'role.visitor.title': 'الزائر',
  'role.visitor.heading': 'بداية واثقة لزيارتك.',
  'role.visitor.intro': 'راجِع دعوتك وأكمِل التسجيل المسبق دون دخول مساحة عمل الموظفين.',
  'role.visitor.boundary': 'معاينة مقيّدة بالدعوة؛ لا دليل عام ولا تسجيل دخول فعلي ولا بطاقة QR ولا اتصال بمزوّد هوية.',
  'role.visitor.primary': 'فتح الدعوة',
  'role.kiosk.title': 'الكشك المشترك',
  'role.kiosk.heading': 'مرحباً بكم في المقر الرئيسي في دبي.',
  'role.kiosk.intro': 'ابحث عن دعوة أو اطلب المساعدة. لا تُعرض أي مساحة عمل للموظفين.',
  'role.kiosk.boundary': 'بحث بالمرجع فقط. ما زالت حماية الرموز والمسح وسياسة الحضور دون موعد والعربية وإدارة الأجهزة المحصّنة قيد الإنجاز.',
  'role.kiosk.primary': 'فتح الكشك',

  // ------------------------------------------------------------ common
  'app.name': 'Passage',
  'app.byline': 'من Scinops',
  'common.loading': 'جارٍ التحميل…',
  'common.cancel': 'إلغاء',
  'common.save': 'حفظ التغييرات',
  'common.saving': 'جارٍ الحفظ…',
  'common.applying': 'جارٍ التطبيق…',
  'common.close': 'إغلاق النافذة',
  'common.tryAgain': 'إعادة المحاولة',
  'common.reference': 'المرجع: {id}',
  'common.reason': 'السبب',
  'common.reasonHint': 'يُسجَّل في سجل تدقيق الإدارة.',
  'common.search': 'بحث / تصفية',
  'common.loadMore': 'تحميل المزيد',
  'common.loadOlder': 'تحميل الأقدم',
  'common.none': '—',
  'common.allSites': 'جميع المواقع',
  'common.organisationWide': 'على مستوى المؤسسة',
  'common.unavailableSite': 'موقع خارج نطاق عرضك',
  'common.yes': 'نعم',
  'common.no': 'لا',
  'common.optional': '(اختياري)',
  'common.scrollHint': 'مرّر أفقياً لعرض كل الأعمدة ←',
  'common.copy': 'نسخ',
  'common.copied': 'تم النسخ',

  'status.active': 'نشط',
  'status.inactive': 'غير نشط',
  'status.disabled': 'معطّل',
  'status.invited': 'مدعو',
  'status.suspended': 'موقوف',
  'status.succeeded': 'نجح',
  'status.denied': 'مرفوض',
  'status.failed': 'فشل',
  'status.effective': 'سارٍ',
  'status.notEffective': 'غير سارٍ',
  'status.revoked': 'ملغى',
  'status.noGrants': 'لا صلاحيات',

  'error.title.forbidden': 'ليس لديك وصول إلى هذا ضمن النطاق الحالي',
  'error.title.notFound': 'غير موجود',
  'error.title.network': 'مشكلة في الاتصال',
  'error.title.generic': 'حدث خطأ ما',
  'error.requestFailed': 'فشل الطلب.',
  'error.requestFailedStatus': 'فشل الطلب (الحالة {status}). أعِد المحاولة، وإذا تكرر ذلك فتواصل مع الدعم.',
  'error.notSaved': 'لم يُحفظ التغيير.',
  'error.network': 'تعذّر الوصول إلى Passage، لذا النتيجة غير معروفة. تحقّق من اتصالك وأعِد المحاولة — إعادة المحاولة لا يمكن أن تطبّق التغيير مرتين.',
  'error.versionConflict.dialog': 'غيّر شخص آخر هذا السجل بعد أن فتحته. أغلِق هذه النافذة لتحميل أحدث نسخة، ثم راجِع تغييرك.',

  'lang.label': 'اللغة',
  'lang.en': 'English',
  'lang.ar': 'العربية',
  'lang.unreviewed': 'النص العربي لم تتم مراجعته بعد',

  // ------------------------------------------------------------ authentication
  'auth.footer': 'تُسجَّل محاولات تسجيل الدخول الفاشلة واستعادة الحسابات في سجل التدقيق.',
  'auth.title': 'تسجيل الدخول',
  'auth.lede': 'استخدم طريقة تسجيل الدخول المعتمدة لدى مؤسستك.',
  'auth.ledeOrg': 'استخدم طريقة تسجيل الدخول المعتمدة لدى {org}.',
  'auth.unknownOrg.title': 'هذا العنوان غير مُعدّ لأي مؤسسة.',
  'auth.unknownOrg.body': 'تحقّق من الرابط الذي حصلت عليه أو تواصل مع المسؤول.',
  'auth.sso': 'المتابعة عبر الدخول الموحّد للمؤسسة',
  'auth.orLocal': 'أو استخدم حساباً مُداراً من المنتج',
  'auth.missing': 'أدخِل البريد الإلكتروني أو اسم المستخدم وكلمة المرور.',
  'auth.email': 'البريد الإلكتروني أو اسم المستخدم',
  'auth.emailPlaceholder': 'name@example.test',
  'auth.password': 'كلمة المرور',
  'auth.passwordPlaceholder': 'أدخِل كلمة المرور',
  'auth.submit': 'تسجيل الدخول',
  'auth.submitting': 'جارٍ تسجيل الدخول…',
  'auth.recovery': 'استعادة الحساب',
  'auth.fineprint1': 'قد تتطلب سياسة الدور المعتمدة التحقق متعدد العوامل.',
  'auth.fineprint2': 'تُدقَّق عمليات الاستعادة والمحاولات الفاشلة.',
  'recovery.title': 'استعادة الحساب',
  'recovery.lede': 'اطلب من مسؤول مؤسستك رابط تفعيل جديداً.',
  'recovery.body': 'يتحقّق المسؤول من هويتك ويُصدر رابطاً صالحاً لمرة واحدة لتعيين كلمة مرور جديدة. لا يوفّر Passage الاستعادة عبر البريد الإلكتروني وحده.',
  'recovery.back': 'العودة إلى تسجيل الدخول',
  'activate.title': 'تعيين كلمة المرور',
  'activate.lede': 'اختر كلمة مرور للحساب {email}.',
  'activate.checking': 'جارٍ التحقق من الرابط…',
  'activate.invalid': 'رابط التفعيل هذا غير صالح أو مستخدم أو منتهي الصلاحية. اطلب رابطاً جديداً من المسؤول.',
  'activate.password': 'كلمة المرور الجديدة',
  'activate.confirm': 'تأكيد كلمة المرور',
  'activate.hint': 'على الأقل {min} حرفاً. العبارة الأطول أقوى.',
  'activate.mismatch': 'كلمتا المرور غير متطابقتين.',
  'activate.submit': 'تعيين كلمة المرور',
  'activate.done': 'تم تعيين كلمة المرور. يمكنك الآن تسجيل الدخول.',

  // ------------------------------------------------------------ shell and navigation
  'shell.skip': 'الانتقال إلى مساحة العمل',
  'shell.yourResponsibilities': 'مسؤولياتك',
  'shell.workspace': 'مساحة العمل',
  'shell.footer': 'تتبع القائمة المسؤوليات الممنوحة لك.',
  'shell.noResponsibilities': 'لا مسؤوليات ممنوحة',
  'shell.openNav': 'فتح القائمة',
  'shell.workingSite': 'موقع العمل',
  'shell.inactiveSuffix': ' (غير نشط)',
  'shell.localTimeAt': 'الوقت المحلي في {site}',
  'shell.signOut': 'تسجيل الخروج',
  'shell.envDev': 'بيئة تطوير محلية',
  'shell.envDevNote': 'بيانات اصطناعية فقط · لا تكاملات فعلية متصلة',
  'shell.planned': 'مخطّط',
  'nav.home': 'مساحة عملي',
  'nav.users': 'المستخدمون والصلاحيات',
  'nav.sites': 'المواقع والقاعات',
  'nav.auditlog': 'سجل التدقيق',
  'nav.policies': 'السياسات وسير العمل',
  'nav.visits': 'الزيارات والدعوات',
  'nav.approvals': 'الموافقات',
  'nav.reception': 'الاستقبال',
  'nav.communications': 'الاتصالات',
  'nav.deliveries': 'التوصيلات',
  'nav.contractors': 'المقاولون والتصاريح',
  'nav.vehicles': 'المركبات والبوابات',
  'nav.credentials': 'البطاقات والدخول',
  'nav.integrations': 'التكاملات والأجهزة',
  'nav.safety': 'الإشغال والتجمّع',
  'nav.summary': 'التقارير الإجمالية',
  'nav.privacy': 'الخصوصية والاحتفاظ',
  'nav.operations': 'عمليات المنصة',
  'nav.security': 'السياسة الأمنية',

  'home.eyebrow': 'Passage / {name}',
  'home.none.title': 'لم تُمنح أي مسؤوليات بعد',
  'home.none.body': 'حسابك نشط لكن دون مسؤوليات ممنوحة، لذا لا توجد مساحة عمل متاحة. اطلب من مسؤول مؤسستك منحك الوصول الذي تحتاجه.',
  'home.openWorkspace': 'فتح مساحة العمل ←',
  'home.notYet': 'غير متاح بعد',
  'home.boundary': 'حدود المسؤولية',
  'planned.title': 'غير منفّذ في هذا الإصدار',
  'planned.body': 'هذه المساحة مجدولة للمرحلة {milestone} (الوحدة {modules}). تُعرض للحفاظ على القائمة المعتمدة؛ لا تُقدَّم هنا سجلات أو إجراءات محاكاة.',
  'notFound.title': 'غير موجود',
  'notFound.body': 'هذه الصفحة غير موجودة أو غير متاحة لك.',
  'notFound.back': 'العودة إلى مساحة عملك',
  'denied.title': 'غير متاح لدورك',
  'denied.body': 'لا تشمل مسؤولياتك الحالية هذه المساحة. إذا كنت بحاجة إليها، فاطلبها من مسؤول مؤسستك.',

  // ------------------------------------------------------------ sites and locations
  'sites.title': 'المواقع والقاعات والأماكن',
  'sites.intro': 'إعدادات خاصة بالموقع ضمن نطاق فعّال واضح.',
  'sites.none.title': 'لا مواقع ضمن نطاقك',
  'sites.none.body': 'ليس لديك وصول على مستوى أي موقع. يمكن لمسؤول المؤسسة منحك ذلك.',
  'sites.loading': 'جارٍ تحميل الموقع…',
  'sites.scope': 'النطاق: {site}',
  'sites.scopeBody': 'تُعرض أماكن هذا الموقع فقط. الوقت المحلي {time} بتاريخ {date} ({zone}، {offset}). تتطلب الإدارة عبر المواقع صلاحية منفصلة.',
  'sites.add': 'إضافة موقع',
  'sites.addTitle': 'إضافة موقع',
  'sites.addSub': 'لكل موقع منطقته الزمنية وأماكنه وقواعد تشغيله.',
  'sites.field.code': 'رمز الموقع',
  'sites.field.codeHint': 'حروف لاتينية كبيرة أو أرقام أو شرطات، مثل DXB-HQ.',
  'sites.deactivate': 'إلغاء تفعيل الموقع…',
  'sites.reactivate': 'إعادة تفعيل الموقع…',
  'sites.deactivateTitle': 'إلغاء تفعيل {name}؟',
  'sites.deactivateSub': 'يجب إلغاء تفعيل جميع الأماكن النشطة أولاً. يُحتفظ بالسجل.',
  'sites.reactivateTitle': 'إعادة تفعيل {name}؟',
  'sites.reactivateSub': 'يُتحقَّق منه مقابل حصة المواقع النشطة لمؤسستك.',
  'sites.tab.locations': 'القاعات والأماكن',
  'sites.tab.settings': 'إعدادات الموقع',
  'sites.tab.branding': 'الهوية البصرية',
  'brand.title': 'الهوية البصرية للموقع',
  'brand.sub': 'تُعرض للزوار في الدعوات والبطاقات والكشك لهذا الموقع.',
  'brand.logo': 'الشعار',
  'brand.logoHint': 'PNG أو JPEG أو WebP أو SVG بسيط، حتى 512 كيلوبايت. يُرفض SVG الذي يحتوي على نصوص برمجية أو مراجع خارجية.',
  'brand.logoNone': 'لم يُرفع أي شعار.',
  'brand.upload': 'رفع الشعار',
  'brand.remove': 'إزالة الشعار',
  'brand.colour': 'لون الهوية',
  'brand.colourHint': 'يُستخدم للأزرار ذات النص الأبيض؛ يجب أن يبلغ التباين 4.5:1 على الأقل.',
  'brand.contrast': 'التباين مع النص الأبيض: {ratio}:1',
  'brand.contrastOk': 'مقروء',
  'brand.contrastLow': 'فاتح جداً',
  'brand.contact': 'سطر التواصل',
  'brand.contactHint': 'مثل رقم هاتف الاستقبال.',
  'brand.preview': 'معاينة',
  'brand.previewBadge': 'معاينة بطاقة الزائر',
  'brand.previewVisitor': 'زائر اصطناعي',
  'brand.save': 'حفظ الهوية البصرية',
  'brand.saved': 'تم حفظ الهوية البصرية (الإصدار {version}).',
  'brand.scanNote': 'تُفحص الملفات المرفوعة من حيث النوع والحجم والمحتوى النشط. فحص البرمجيات الخبيثة غير متصل في هذا الإصدار.',
  'loc.field.picture': 'الصورة',
  'loc.field.pictureHint': 'PNG أو JPEG أو WebP، حتى 5 ميغابايت.',
  'loc.picture.upload': 'رفع صورة',
  'loc.picture.remove': 'إزالة الصورة',
  'loc.picture.alt': 'صورة {name}',
  'sites.tabsLabel': 'أقسام الموقع',
  'kind.campus': 'مجمّع',
  'kind.building': 'مبنى',
  'kind.floor': 'طابق',
  'kind.zone': 'منطقة',
  'kind.gate': 'بوابة',
  'kind.room': 'قاعة',
  'kind.desk': 'مكتب استقبال',
  'kind.parking': 'موقف سيارات',
  'kind.other': 'وجهة أخرى',
  'loc.register': 'سجل الأماكن',
  'loc.registerSub': 'يبقى البحث والتحديد ضمن نطاق هذا الموقع.',
  'loc.add': '+ إضافة مكان',
  'loc.searchPlaceholder': 'ابحث في أماكن النطاق الحالي',
  'loc.loading': 'جارٍ تحميل الأماكن…',
  'loc.none.filtered': 'لا أماكن مطابقة',
  'loc.none': 'لا أماكن بعد',
  'loc.none.filteredBody': 'غيّر البحث لعرض المزيد من هذا الموقع.',
  'loc.none.createBody': 'أضِف أول مبنى أو بوابة أو قاعة لهذا الموقع.',
  'loc.none.readBody': 'لم تُعدّ أي أماكن لهذا الموقع.',
  'loc.tableLabel': 'جدول الأماكن',
  'loc.col.location': 'المكان',
  'loc.col.type': 'النوع / المكان الأب',
  'loc.col.capacity': 'السعة',
  'loc.col.status': 'الحالة',
  'loc.col.action': 'الإجراء',
  'loc.parentFallback': 'المكان الأب',
  'loc.edit': 'تعديل',
  'loc.editLabel': 'تعديل {name}',
  'loc.deactivate': 'إلغاء التفعيل…',
  'loc.reactivate': 'إعادة التفعيل…',
  'loc.deactivateLabel': 'مراجعة إلغاء تفعيل {name}',
  'loc.reactivateLabel': 'إعادة تفعيل {name}',
  'loc.count': { zero: 'لا أماكن في {site}', one: 'مكان واحد في {site}', two: 'مكانان في {site}', few: '{count} أماكن في {site}', many: '{count} مكاناً في {site}', other: '{count} مكان في {site}' },
  'loc.truncated': ' (القائمة مقتطعة — حسّن البحث)',
  'loc.before.title': 'قبل إلغاء التفعيل',
  'loc.before.deps': 'فحص الاعتماديات',
  'loc.before.depsBody': 'الأماكن الفرعية النشطة والزيارات المستقبلية وربط مناطق الدخول تمنع التعطيل حتى تُعالج. القواعد التي تذكر المكان (نطاقات المعتمدين وإرشادات الوصول) تُعرض وتحتاج إلى تأكيد.',
  'loc.before.impact': 'معاينة الأثر',
  'loc.before.impactBody': 'تُعرض السجلات المتأثرة قبل تطبيق أي تغيير، ويُكتشف أي تغيير متزامن عند التطبيق.',
  'loc.before.recovery': 'الاستعادة',
  'loc.before.recoveryBody': 'لا تُحذف الأماكن أبداً. يحتفظ إلغاء التفعيل بالسجل ويمكن عكسه عندما يكون المكان الأب نشطاً.',
  'loc.dialog.add': 'إضافة مكان',
  'loc.dialog.edit': 'تعديل {name}',
  'loc.dialog.savedTo': 'يُحفظ في {site} فقط.',
  'loc.field.type': 'النوع',
  'loc.field.parent': 'المكان الأب',
  'loc.field.parentHint': 'اتركه فارغاً للإلحاق بالموقع مباشرة.',
  'loc.field.parentSite': '{site} (الموقع)',
  'loc.field.name': 'الاسم',
  'loc.field.code': 'الرمز (اختياري)',
  'loc.field.codeHint': 'حروف أو أرقام أو نقاط أو شرطات سفلية أو شرطات.',
  'loc.field.capacity': 'السعة (اختياري)',
  'loc.field.capacityHint': 'عدد الأشخاص الذين تتسع لهم المساحة.',
  'loc.submit.add': 'إضافة المكان',
  'loc.status.deactivateTitle': 'إلغاء تفعيل {name}؟',
  'loc.status.reactivateTitle': 'إعادة تفعيل {name}؟',
  'loc.status.sub': 'راجِع الأثر قبل التطبيق.',
  'loc.status.checking': 'جارٍ فحص الاعتماديات…',
  'loc.status.blocked': { one: 'محظور: يوجد مكان نشط واحد بداخله', two: 'محظور: يوجد مكانان نشطان بداخله', few: 'محظور: توجد {count} أماكن نشطة بداخله', many: 'محظور: يوجد {count} مكاناً نشطاً بداخله', other: 'محظور: يوجد {count} مكان نشط بداخله' },
  'loc.status.blockedBody': 'ألغِ تفعيل هذه الأماكن أو انقلها أولاً:',
  'loc.status.clear': 'لا أماكن فرعية نشطة.',
  'loc.status.unchecked': 'لم يُفحص بعد (وحدات غير منفّذة): {list}.',
  'loc.status.reactivateNote': 'يجب أن يكون المكان الأب نشطاً. يُحتفظ بالسجل في الحالتين.',
  'loc.status.submitDeactivate': 'إلغاء تفعيل المكان',
  'loc.status.submitReactivate': 'إعادة تفعيل المكان',
  'settings.title': 'إعدادات الموقع',
  'settings.meta': 'الرمز {code} · الإصدار {version}',
  'settings.editable': 'يمكنك تعديل هذا الموقع.',
  'settings.readOnly': 'للقراءة فقط لدورك.',
  'settings.conflict': 'حفظ شخص آخر هذا الموقع بعد أن فتحته.',
  'settings.reload': 'تحميل أحدث نسخة',
  'settings.conflictAfter': 'ستُستبدل تعديلاتك غير المحفوظة؛ راجِعها مجدداً بعد ذلك.',
  'settings.saved': 'تم الحفظ. الموقع الآن في الإصدار {version}.',
  'settings.name': 'اسم الموقع',
  'settings.tz': 'المنطقة الزمنية (IANA)',
  'settings.tzHint': 'تحتفظ الجداول بلحظاتها المسجّلة إذا تغيّر هذا.',
  'settings.country': 'الدولة (رمز ISO)',
  'settings.phone': 'هاتف الاستقبال',
  'settings.email': 'بريد الاستقبال',
  'settings.address': 'العنوان',
  'settings.instructions': 'تعليمات الزوار',
  'settings.tzReview': 'تغيير المنطقة الزمنية يتطلب مراجعة الأثر',
  'settings.tzReviewBody': 'تحتفظ السجلات الحالية بلحظاتها المسجّلة بالتوقيت العالمي وستُعرض بالمنطقة الجديدة. لا يمكن عدّ الزيارات المستقبلية بعد لأن تخطيط الزيارات غير منفّذ.',
  'settings.tzAck': 'راجعتُ أثر التغيير من {from} إلى {to}.',
  'settings.submit': 'حفظ إعدادات الموقع',

  // ------------------------------------------------------------ users and access
  'users.eyebrow': 'Passage / الإدارة',
  'users.title': 'المستخدمون والصلاحيات',
  'users.intro': 'امنح الحد الأدنى من النطاق اللازم لكل مسؤولية.',
  'users.note.title': 'الأدوار لا تعني الوصول إلى المستندات',
  'users.note.body': 'معاينات الهوية والتصدير والتجاوزات والصلاحيات الحساسة الأخرى تأتي فقط من الأدوار المتخصصة، ولا يسندها إلا المسؤولون المخوَّلون.',
  'users.panel': 'الأشخاص أصحاب الوصول',
  'users.panelSub': 'الحسابات في مؤسستك.',
  'users.searchPlaceholder': 'ابحث في الحسابات المحمّلة',
  'users.loading': 'جارٍ تحميل المستخدمين…',
  'users.none': 'لا حسابات مطابقة',
  'users.tableLabel': 'جدول المستخدمين',
  'users.col.user': 'المستخدم',
  'users.col.responsibilities': 'المسؤوليات',
  'users.col.state': 'الحالة',
  'users.col.action': 'الإجراء',
  'users.preview': 'معاينة الوصول',
  'users.previewLabel': 'معاينة وصول {name}',
  'users.manage': 'إدارة',
  'users.manageLabel': 'إدارة {name}',
  'users.loaded': { zero: 'لم يُحمَّل أي حساب', one: 'حُمّل حساب واحد', two: 'حُمّل حسابان', few: 'حُمّلت {count} حسابات', many: 'حُمّل {count} حساباً', other: 'حُمّل {count} حساب' },
  'users.review': 'مراجعة الصلاحيات',
  'users.reviewHint': 'اختر «معاينة الوصول» لمعرفة ما يمكن للشخص فعله، كما يحسبه الخادم من صلاحياته الحالية.',
  'users.create': 'إضافة مستخدم',
  'access.user': 'المستخدم',
  'access.orgWide': 'على مستوى المؤسسة',
  'access.nothingOrgWide': 'لا شيء على مستوى المؤسسة.',
  'access.notGranted': 'غير ممنوح',
  'access.notGrantedBody': 'كل ما لم يُذكر مرفوض افتراضياً، بما في ذلك الصلاحيات الحساسة.',
  'access.hiddenScopes': 'لا تُعرض الصلاحيات على المواقع التي لا تديرها.',
  'user.eyebrow': 'Passage / المستخدمون والصلاحيات',
  'user.loading': 'جارٍ تحميل المستخدم…',
  'user.grant': 'منح مسؤولية',
  'user.grantSpecialist': 'إسناد دور متخصص',
  'user.revokeSessions': 'إنهاء الجلسات ({count})',
  'user.deactivate': 'تعطيل…',
  'user.resetPassword': 'إصدار رابط تفعيل…',
  'user.self': 'لا يمكنك تغيير صلاحياتك بنفسك. يجب أن يقوم بذلك مسؤول آخر.',
  'user.grants': 'الصلاحيات',
  'user.grantsSub': 'الصلاحيات الحالية والسابقة. يُحتفظ بالملغاة والمنتهية للمساءلة.',
  'user.grantsLabel': 'جدول الصلاحيات',
  'user.col.responsibility': 'المسؤولية',
  'user.col.scope': 'النطاق',
  'user.col.validity': 'الصلاحية الزمنية',
  'user.col.state': 'الحالة',
  'user.col.action': 'الإجراء',
  'user.noGrants': 'لم تُمنح أي صلاحيات لهذا المستخدم.',
  'user.kind.experience': 'تجربة',
  'user.kind.capability': 'قدرة',
  'user.kind.specialist': 'دور متخصص',
  'user.from': 'من {date}',
  'user.until': 'حتى {date}',
  'user.noEnd': 'بلا تاريخ انتهاء',
  'user.revoke': 'إلغاء…',
  'user.revokeLabel': 'إلغاء {role} في {scope}',
  'user.effective': 'الوصول الفعلي',
  'user.revokeTitle': 'إلغاء {role}؟',
  'user.revokeSub': '{name} · {scope}. يسري عند طلبه التالي.',
  'user.revokeConfirm': 'إلغاء الصلاحية',
  'user.deactivateTitle': 'تعطيل {name}؟',
  'user.deactivateSub': 'تنتهي جلساته فوراً. يُحتفظ بالصلاحيات والسجل.',
  'user.deactivateConfirm': 'تعطيل المستخدم',
  'user.sessionsTitle': 'إنهاء جميع الجلسات؟',
  'user.sessionsSub': 'سيحتاج {name} إلى تسجيل الدخول مجدداً على كل جهاز.',
  'user.sessionsConfirm': 'إنهاء الجلسات',
  'grant.title': 'منح مسؤولية إلى {name}',
  'grant.titleSpecialist': 'إسناد دور متخصص إلى {name}',
  'grant.sub': 'يسري عند طلبه التالي. يُسجَّل في سجل تدقيق الإدارة.',
  'grant.experience': 'التجربة',
  'grant.specialist': 'الدور المتخصص',
  'grant.scope': 'النطاق',
  'grant.scopeOrg': 'جميع المواقع (على مستوى المؤسسة)',
  'grant.scopeSiteOnly': 'هذه التجربة خاصة بموقع محدد دائماً.',
  'grant.ends': 'تاريخ الانتهاء',
  'grant.endsOptional': 'تاريخ الانتهاء (اختياري)',
  'grant.endsHint': 'بالتوقيت المحلي لجهازك.',
  'grant.endsRequired': 'مطلوب: الوصول الذي تمنحه بصفتك مسؤول موقع يجب أن ينتهي خلال {days} يوماً.',
  'grant.submit': 'منح المسؤولية',
  'grant.submitSpecialist': 'إسناد الدور المتخصص',
  'grant.submitting': 'جارٍ المنح…',
  'activation.title': 'رابط التفعيل لـ {name}',
  'activation.body': 'سلّم هذا الرابط الصالح لمرة واحدة إلى {name} عبر قناة موثوقة. تنتهي صلاحيته {expires}. يُعرض مرة واحدة فقط.',
  'activation.issueTitle': 'إصدار رابط تفعيل جديد؟',
  'activation.issueSub': 'يتوقف أي رابط سابق عن العمل ويُسجَّل خروج {name} من كل الأجهزة.',
  'activation.issueConfirm': 'إصدار الرابط',
  'createUser.title': 'إضافة مستخدم',
  'createUser.sub': 'ينشئ Passage الحساب ورابط تفعيل صالحاً لمرة واحدة. امنح المسؤوليات بعد ذلك.',
  'createUser.email': 'البريد الإلكتروني',
  'createUser.name': 'الاسم المعروض',
  'createUser.submit': 'إنشاء المستخدم',

  // ------------------------------------------------------------ audit
  'audit.eyebrow': 'Passage / المدقّق',
  'audit.title': 'سجل التدقيق',
  'audit.intro': 'تتبّع الأدلة دون تغييرها. السجلات قابلة للإضافة فقط.',
  'audit.panel': 'الأحداث ضمن نطاقك',
  'audit.panelSub': 'الأحدث أولاً. تظهر القيم قبل/بعد فقط مع قدرة التدقيق الحساس المنفصلة.',
  'audit.loading': 'جارٍ تحميل أحداث التدقيق…',
  'audit.none': 'لا أحداث تدقيق ضمن نطاقك بعد',
  'audit.tableLabel': 'أحداث التدقيق',
  'audit.col.when': 'الوقت (جهازك)',
  'audit.col.action': 'الإجراء',
  'audit.col.where': 'المكان',
  'audit.col.outcome': 'النتيجة',
  'audit.col.reason': 'السبب / المرجع',
  'audit.organisation': 'المؤسسة',
  'audit.loaded': { zero: 'لم يُحمَّل أي حدث', one: 'حُمّل حدث واحد', two: 'حُمّل حدثان', few: 'حُمّلت {count} أحداث', many: 'حُمّل {count} حدثاً', other: 'حُمّل {count} حدث' },

  // ------------------------------------------------------------ security policy
  'policy.title': 'السياسة الأمنية',
  'policy.intro': 'قواعد تسجيل الدخول والجلسات السارية في مؤسستك. يغيّرها مسؤول النظام فقط.',
  'policy.source.platform': 'الإعداد الافتراضي للمنصة',
  'policy.source.organisation': 'إعداد خاص بالمؤسسة',
  'policy.sessionIdle': 'مهلة خمول الجلسة (دقائق)',
  'policy.sessionAbsolute': 'أقصى مدة للجلسة (ساعات)',
  'policy.maxFailures': 'محاولات الدخول الفاشلة قبل القفل (لكل حساب)',
  'policy.window': 'نافذة احتساب المحاولات الفاشلة (دقائق)',
  'policy.lock': 'مدة القفل (دقائق)',
  'policy.networkFailures': 'محاولات الدخول الفاشلة قبل القفل (لكل شبكة)',
  'policy.passwordMin': 'الحد الأدنى لطول كلمة المرور',
  'policy.activationHours': 'صلاحية رابط التفعيل (ساعات)',
  'policy.siteGrantMaxDays': 'أطول وصول يمكن لمسؤول الموقع منحه (أيام)',
  'policy.version': 'إصدار السياسة {version}',

  // ------------------------------------------------------------ platform console
  'platform.eyebrow': 'Passage / إدارة النظام',
  'platform.title': 'إدارة النظام',
  'platform.signInLede': 'تسجيل دخول مسؤول النظام. يسجّل مستخدمو المؤسسات الدخول من عنوان مؤسستهم.',
  'platform.nav.organisations': 'المؤسسات',
  'platform.nav.policy': 'السياسة الأمنية للمنصة',
  'platform.orgs.title': 'المؤسسات',
  'platform.orgs.intro': 'أنشئ المؤسسات وعناوين تسجيل الدخول الخاصة بها واستحقاقاتها. تبقى بيانات كل مؤسسة داخلها.',
  'platform.orgs.create': 'إضافة مؤسسة',
  'platform.orgs.col.name': 'المؤسسة',
  'platform.orgs.col.addresses': 'عناوين تسجيل الدخول',
  'platform.orgs.col.sites': 'المواقع النشطة',
  'platform.orgs.col.entitlements': 'الاستحقاقات',
  'platform.orgs.col.status': 'الحالة',
  'platform.org.slug': 'المعرّف',
  'platform.org.name': 'اسم المؤسسة',
  'platform.org.hostname': 'عنوان تسجيل الدخول (اسم المضيف)',
  'platform.org.multiSite': 'تعدد المواقع مفعّل',
  'platform.org.maxSites': 'الحد الأقصى للمواقع النشطة',
  'platform.org.maxSitesHint': 'اتركه فارغاً لعدم وجود حد.',
  'platform.org.firstAdminEmail': 'بريد المسؤول الأول',
  'platform.org.firstAdminName': 'اسم المسؤول الأول',
  'platform.org.entitlements': 'الاستحقاقات',
  'platform.org.policy': 'السياسة الأمنية للمؤسسة',
  'platform.org.policyInherit': 'تستخدم الإعداد الافتراضي للمنصة.',
  'platform.org.policyOverride': 'تجاوز الإعداد الافتراضي للمنصة',
  'platform.org.policyClear': 'العودة إلى الإعداد الافتراضي للمنصة',
  'platform.org.addAdmin': 'إضافة مسؤول',
  'platform.org.adminEmail': 'بريد المسؤول',
  'platform.org.adminName': 'اسم المسؤول',
  'platform.org.users': 'المسؤولون',
  'platform.org.suspend': 'إيقاف المؤسسة',
  'platform.org.reactivate': 'إعادة تفعيل المؤسسة',
  'platform.policy.title': 'السياسة الأمنية للمنصة',
  'platform.policy.intro': 'الإعداد الافتراضي لكل مؤسسة دون تجاوز. تسري التغييرات عند تسجيل الدخول التالي؛ تحتفظ الجلسات الحالية بمدة صلاحيتها.',

  'specialist.identity-verifier': 'مُتحقّق الهوية',
  'specialist.credential-issuer': 'مُصدر بطاقات الدخول',
  'specialist.privacy-authority': 'سلطة الخصوصية',
  'specialist.report-exporter': 'مُصدِّر التقارير',
  'specialist.sensitive-auditor': 'قارئ التدقيق الحساس',
  'specialist.muster-exporter': 'مُصدِّر قوائم التجمّع',
  'specialist.approval-coordinator': 'منسّق الموافقات',
  'specialist.delegated-host': 'مضيف مفوَّض',
  'specialist.company-suspension': 'سلطة إيقاف المقاولين',

  'mfa.title': 'التحقق بخطوتين',
  'mfa.verify.lede': 'أدخِل الرمز المكوّن من 6 أرقام من تطبيق المصادقة.',
  'mfa.verify.code': 'رمز المصادقة',
  'mfa.verify.useRecovery': 'استخدام رمز استرداد بدلاً من ذلك',
  'mfa.verify.useCode': 'استخدام رمز تطبيق المصادقة بدلاً من ذلك',
  'mfa.verify.recovery': 'رمز الاسترداد',
  'mfa.verify.submit': 'تحقّق',
  'mfa.enrol.title': 'إعداد التحقق بخطوتين',
  'mfa.enrol.lede': 'يتطلب دورك خطوة ثانية. امسح الرمز بتطبيق مصادقة، ثم أدخِل الرمز المكوّن من 6 أرقام الذي يظهره.',
  'mfa.enrol.ledeOptional': 'امسح الرمز بتطبيق مصادقة، ثم أدخِل الرمز المكوّن من 6 أرقام الذي يظهره.',
  'mfa.enrol.manual': 'أو أدخِل هذا المفتاح يدوياً',
  'mfa.enrol.submit': 'تأكيد وتفعيل',
  'mfa.codes.title': 'احفظ رموز الاسترداد',
  'mfa.codes.body': 'يعمل كل رمز مرة واحدة إذا فقدت جهازك. تُعرض الآن فقط — احفظها في مكان آمن.',
  'mfa.codes.done': 'لقد حفظتها',
  'mfa.account.title': 'أمان الحساب',
  'mfa.account.status.on': 'التحقق بخطوتين مفعّل.',
  'mfa.account.status.off': 'التحقق بخطوتين غير مفعّل.',
  'mfa.account.required': 'مطلوب لدورك وفق سياسة مؤسستك.',
  'mfa.account.turnOn': 'تفعيل التحقق بخطوتين',
  'mfa.account.turnOff': 'إيقاف',
  'mfa.account.newCodes': 'رموز استرداد جديدة',
  'mfa.account.confirmCode': 'أدخِل رمزاً حالياً للتأكيد',
  'mfa.reset': 'إعادة تعيين التحقق بخطوتين…',
  'mfa.resetTitle': 'إعادة تعيين التحقق بخطوتين لـ {name}؟',
  'mfa.resetSub': 'يُستخدم عند فقدان الجهاز. تنتهي جلساته ويُعدّ عاملاً جديداً عند تسجيل الدخول التالي.',
  'mfa.resetConfirm': 'إعادة تعيين',
  'policy.mfaRequirement': 'التحقق بخطوتين مطلوب لـ',
  'policy.mfa.none': 'لا أحد (اختياري للجميع)',
  'policy.mfa.administrators': 'المسؤولين وأصحاب الأدوار المتخصصة',
  'policy.mfa.all': 'الجميع',
  // ------------------------------------------------------------ API messages
  'api.unauthenticated': 'سجّل الدخول للمتابعة.',
  'api.notFound': 'السجل المطلوب غير موجود.',
  'api.forbidden': 'ليست لديك صلاحية لهذا الإجراء ضمن النطاق الحالي.',
  'api.validation': 'بعض التفاصيل تحتاج إلى تصحيح.',
  'api.versionConflict': 'تغيّر هذا السجل منذ تحميله. أعِد التحميل وراجِع قبل الحفظ مجدداً.',
  'api.unknownOrganisation': 'هذا العنوان غير مُعدّ لأي مؤسسة.',
  'api.originRejected': 'مصدر هذا الطلب غير مسموح.',
  'api.csrfRejected': 'تعذّر التحقق من الطلب. أعِد تحميل الصفحة وحاول مجدداً.',
  'api.idempotencyRequired': 'يحتاج هذا الأمر إلى ترويسة Idempotency-Key حتى لا تكرّره إعادة المحاولة.',
  'api.idempotencyInvalid': 'قيمة Idempotency-Key غير صالحة.',
  'api.idempotencyReused': 'استُخدم مفتاح Idempotency-Key هذا من قبل لطلب مختلف.',
  'api.invalidCredentials': 'لم تُقبل بيانات تسجيل الدخول. تحقّق منها وحاول مجدداً.',
  'api.rateLimited': 'طلبات كثيرة خلال وقت قصير. انتظر قليلاً ثم حاول مجدداً.',
  'api.signInRateLimited': 'محاولات تسجيل دخول كثيرة. انتظر قبل المحاولة مجدداً.',
  'api.unsupportedMediaType': 'أرسِل جسم الطلب بصيغة JSON.',
  'api.unreadable': 'تعذّرت قراءة الطلب.',
  'api.methodNotAllowed': 'هذا الإجراء غير متاح هنا.',
  'api.requestConflict': 'يتعارض هذا الطلب مع الحالة الحالية. أعد التحميل وحاول مجدداً.',
  'api.requestRejected': 'تم رفض الطلب.',
  'api.internal': 'حدث خطأ ما. اذكر المرجع إذا تواصلت مع الدعم.',
  'api.selfAccess': 'لا يمكنك تغيير صلاحياتك بنفسك.',
  'api.entitlementMultiSite': 'تحتاج مؤسستك إلى استحقاق تعدد المواقع لإضافة موقع نشط آخر.',
  'api.siteLimit': 'بلغت مؤسستك حدها الأقصى البالغ {max} من المواقع النشطة.',
  'api.tzImpact': 'تغيير المنطقة الزمنية للموقع يتطلب مراجعة الأثر. تحتفظ السجلات الحالية بلحظاتها المسجّلة.',
  'api.siteInactive': 'هذا الموقع غير نشط.',
  'api.alreadyStatus': 'هذا السجل بالفعل في الحالة: {status}.',
  'api.locationChildren': 'ألغِ تفعيل الأماكن النشطة بداخله أو انقلها أولاً.',
  'api.siteHasLocations': 'ألغِ تفعيل الأماكن النشطة في المستوى الأعلى لهذا الموقع أولاً.',
  'api.mfaInvalid': 'لم يُقبل هذا الرمز. تحقّق من تطبيق المصادقة وحاول مجدداً.',
  'api.mfaChallengeExpired': 'انتهت صلاحية خطوة تسجيل الدخول. سجّل الدخول مجدداً.',
  'api.mfaRequiredByPolicy': 'تشترط مؤسستك التحقق متعدد العوامل لدورك، لذا لا يمكن إيقافه.',
  'api.payloadTooLarge': 'الملف المرفوع كبير جداً.',
  'api.userDisabled': 'هذا المستخدم معطّل.',
  'api.grantExists': 'يملك المستخدم هذه الصلاحية في هذا النطاق بالفعل.',
  'api.grantRevoked': 'هذه الصلاحية ملغاة بالفعل.',
  'api.lastAdmin': 'سيترك هذا المؤسسة دون مسؤول فعّال. امنح مسؤولاً آخر أولاً.',
  'api.organisationSuspended': 'هذه المؤسسة موقوفة.',
  'api.userExists': 'يوجد مستخدم بهذا البريد الإلكتروني بالفعل.',
  'api.organisationExists': 'توجد مؤسسة بهذا المعرّف أو العنوان بالفعل.',

  // ------------------------------------------------------------ validation
  'v.required': 'مطلوب.',
  'v.tooLong': 'بحد أقصى {max} حرفاً.',
  'v.tooShort': 'على الأقل {min} حرفاً.',
  'v.invalid': 'هذه القيمة غير صالحة.',
  'v.mfaBelowFloor': 'يمكن للمؤسسة أن تشترط مصادقة متعددة العوامل أكثر من الحد الأدنى للمنصة، ولا يمكنها أن تشترط أقل منه.',
  'v.number': 'أدخِل عدداً صحيحاً بين {min} و{max}.',
  'v.email': 'أدخِل بريداً إلكترونياً صالحاً.',
  'v.siteCode': 'استخدم من 1 إلى 32 حرفاً لاتينياً كبيراً أو رقماً أو شرطة.',
  'v.locationCode': 'استخدم من 1 إلى 32 حرفاً أو رقماً أو نقطة أو شرطة سفلية أو شرطة.',
  'v.countryCode': 'استخدم رمز دولة من حرفين.',
  'v.timeZone': 'اختر منطقة زمنية صالحة من IANA، مثل Asia/Dubai.',
  'v.time': 'استخدم صيغة 24 ساعة، مثل 08:30.',
  'v.cursor': 'مؤشر غير صالح.',
  'v.siteCodeTaken': 'يستخدم موقع آخر هذا الرمز بالفعل.',
  'v.locationCodeTaken': 'يستخدم مكان آخر في هذا الموقع هذا الرمز بالفعل.',
  'v.parentRequired': 'يجب أن يكون هذا النوع ({kind}) داخل مكان أب.',
  'v.parentSelf': 'لا يمكن أن يكون المكان أباً لنفسه.',
  'v.parentNotInSite': 'اختر مكاناً أباً في هذا الموقع.',
  'v.parentKind': 'لا يمكن وضع هذا النوع ({kind}) تحت النوع ({parent}).',
  'v.parentInactive': 'المكان الأب غير نشط.',
  'v.cycle': 'سيضع هذا النقل المكان داخل نفسه.',
  'v.capacityNotApplicable': 'السعة لا تنطبق على هذا النوع من الأماكن.',
  'v.grantUnknown': 'اختر تجربة أو دوراً متخصصاً أو قدرة معروفة.',
  'v.grantNotStaff': 'تجربتا الزائر والكشك نوعا جلسة، وليستا صلاحيات للموظفين.',
  'v.grantSiteRequired': 'يجب منح هذه التجربة لموقع محدد.',
  'v.grantHumanOnly': 'لا يمكن لهويات الأتمتة امتلاك صلاحيات الموافقة أو بطاقات الدخول أو البوابات أو الحضور.',
  'v.grantOrgOnly': 'تنطبق هذه القدرة على مستوى المؤسسة؛ لا تختر موقعاً.',
  'v.grantSiteNotFound': 'اختر موقعاً في هذه المؤسسة.',
  'v.grantFuture': 'يجب أن يكون تاريخ الانتهاء في المستقبل.',
  'v.grantSiteAdminScope': 'يمكنك منح الوصول فقط على المواقع التي تديرها.',
  'v.grantSiteAdminCode': 'لا يمكن لمسؤولي المواقع منح هذه المسؤولية.',
  'v.grantEndRequired': 'يجب أن ينتهي الوصول الذي تمنحه خلال {days} يوماً.',
  'v.passwordShort': 'استخدم {min} حرفاً على الأقل.',
  'v.passwordEmail': 'يجب ألا تكون كلمة المرور هي بريدك الإلكتروني.',
  'v.slug': 'استخدم من 1 إلى 64 حرفاً لاتينياً صغيراً أو رقماً أو شرطة.',
  'v.hostname': 'أدخِل اسم مضيف مثل example.passage.test.',
  'v.otp': 'أدخِل الرمز المكوّن من 6 أرقام من تطبيق المصادقة.',
  'v.colour': 'استخدم لوناً بالصيغة #rrggbb.',
  'v.contrast': 'هذا اللون فاتح جداً للنص الأبيض (التباين {ratio}:1؛ المطلوب 4.5:1 على الأقل).',
  'v.uploadEmpty': 'الملف فارغ.',
  'v.uploadTooLarge': 'حجم الملف أكبر من {max}.',
  'v.uploadType': 'استخدم صورة PNG أو JPEG أو WebP{svg}.',
  'v.uploadMismatch': 'محتوى الملف لا يطابق نوعه.',
  'v.uploadUnsafeSvg': 'يحتوي ملف SVG هذا على نصوص برمجية أو مراجع خارجية أو محتوى نشط ولا يمكن استخدامه.',
  'v.uploadMalformed': 'تعذّرت قراءة الملف كصورة.',
  'v.range': 'أدخِل قيمة بين {min} و{max}.',
};
